import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

/**
 * Responsive accordion custom element.
 *
 * Supports:
 * - Different default states on mobile and desktop
 * - Disabling accordion interaction per breakpoint
 * - Closing with Escape
 * - Explicit close targets using [data-accordion-close]
 *
 * Important:
 * This intentionally extends HTMLElement instead of Component
 * because parent components may rely on accessing its DOM directly.
 */
class AccordionCustom extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  /** @returns {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) {
      throw new Error('AccordionCustom: <details> element not found.');
    }

    return details;
  }

  /** @returns {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) {
      throw new Error('AccordionCustom: <summary> element not found.');
    }

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  connectedCallback() {
    /* Prevent duplicated listeners if the element reconnects. */
    this.#controller?.abort();
    this.#controller = new AbortController();

    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });

    this.summary.addEventListener('click', this.#handleSummaryClick, {
      signal,
    });

    this.details.addEventListener('click', this.#handleContentClick, {
      signal,
    });

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, {
      signal,
    });
  }

  disconnectedCallback() {
    this.#controller?.abort();
    this.#controller = null;
  }

  /**
   * Determines whether the accordion should behave interactively
   * at the current breakpoint.
   *
   * @returns {boolean}
   */
  #isInteractionDisabled() {
    const isMobile = isMobileBreakpoint();

    return isMobile ? this.#disableOnMobile : this.#disableOnDesktop;
  }

  /**
   * Handles summary interaction.
   *
   * @param {Event} event
   */
  #handleSummaryClick = (event) => {
    if (!this.#isInteractionDisabled()) return;

    event.preventDefault();
  };

  /**
   * Handles explicit close targets inside the accordion.
   *
   * Any element containing [data-accordion-close] can close
   * the current accordion.
   *
   * @param {MouseEvent} event
   */
  #handleContentClick = (event) => {
    if (!this.details.open) return;
    if (!(event.target instanceof Element)) return;

    const closeTarget = event.target.closest('[data-accordion-close]');

    if (!closeTarget || !this.contains(closeTarget)) return;

    this.#close({
      restoreFocus: true,
      preventScroll: true,
    });
  };

  /**
   * Recalculates the default state when crossing the theme breakpoint.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Applies responsive default state.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    const shouldOpen = isMobile
      ? this.hasAttribute('open-by-default-on-mobile')
      : this.hasAttribute('open-by-default-on-desktop');

    this.details.open = shouldOpen;
  }

  /**
   * Handles keyboard interaction.
   *
   * @param {KeyboardEvent} event
   */
  #handleKeyDown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#closeWithEscape) return;
    if (!this.details.open) return;

    event.preventDefault();

    this.#close({
      restoreFocus: true,
      preventScroll: false,
    });
  };

  /**
   * Closes the accordion.
   *
   * @param {Object} [options]
   * @param {boolean} [options.restoreFocus=true]
   * @param {boolean} [options.preventScroll=true]
   */
  #close({ restoreFocus = true, preventScroll = true } = {}) {
    if (!this.details.open) return;

    this.details.open = false;

    if (restoreFocus) {
      this.summary.focus({ preventScroll });
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}